import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  tableCellClasses,
  Stack,
  LinearProgress,
  IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchCostCentreReport, downloadCostCentreReportExcel } from '../../store/reportStore/reportThunk';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import Filters from '../../components/Filters/Filters';
import CostCentreSelector from '../../components/CostCentre/CostCentreSelector';
import { toast } from 'react-toastify';
import styled_ from "@emotion/styled";
import { saveAs } from 'file-saver';
import { Utils } from '../../utils/common-utils';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0,
    backgroundColor: '#fff',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:last-child td, &:last-child th': {},
}));

interface LedgerBreakdown {
  ledgerId: string;
  ledgerName: string;
  primaryGroupCode: string;
  totalDebit: number;
  totalCredit: number;
  transactionCount: number;
  netAmount: number;
}

interface CostCentreData {
  costCentreId: string;
  costCentreName: string;
  costCentreAlias: string;
  costCentreCategory: string | null;
  categoryName: string | null;
  totalIncome: number;
  totalExpense: number;
  netProfitLoss: number;
  totalTransactions: number;
  ledgers: LedgerBreakdown[];
}

interface Summary {
  totalCostCentres: number;
  totalIncome: number;
  totalExpense: number;
  totalNetProfitLoss: number;
  totalTransactions: number;
  highestExpenseCostCentre: { name: string; amount: number } | null;
  highestIncomeCostCentre: { name: string; amount: number } | null;
}

interface CostCentreReportData {
  data: CostCentreData[];
  summary: Summary;
  filters: {
    startDate: string | null;
    endDate: string | null;
    costCentreId: string | null;
  };
}

const CostCentreReportPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const accountId = selectedAccount?._id;

  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState<CostCentreReportData | null>(null);
  const [selectedCostCentre, setSelectedCostCentre] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Fetch Cost Centre Report
  const fetchReport = () => {
    if (!accountId || !filters) return;

    setIsLoading(true);
    const startDate = filterData?.startDate?.getTime();
    const endDate = filterData?.endDate?.getTime();

    dispatch(fetchCostCentreReport({
      startDate: startDate?.toString(),
      endDate: endDate?.toString(),
      costCentreId: selectedCostCentre || undefined // Server-side filter by cost centre
    }))
      .unwrap()
      .then((response: any) => {
        setReportData(response);
        setIsLoading(false);
      })
      .catch((error: any) => {
        console.error('Error fetching cost centre report:', error);
        toast.error('Failed to load cost centre report');
        setIsLoading(false);
      });
  };

  // Auto-fetch report when filters or cost centre selection change
  useEffect(() => {
    if (filters && accountId) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedCostCentre]);

  // Download Excel Report
  const handleDownloadExcel = () => {
    if (!filterData) {
      toast.warning('Please apply filters first');
      return;
    }

    const startDate = filterData?.startDate?.getTime();
    const endDate = filterData?.endDate?.getTime();

    dispatch(downloadCostCentreReportExcel({
      startDate: startDate?.toString(),
      endDate: endDate?.toString(),
      costCentreId: selectedCostCentre || undefined
    }))
      .unwrap()
      .then((blob: Blob) => {
        saveAs(blob, `Cost_Centre_Report_${Date.now()}.xlsx`);
        toast.success('Excel file downloaded successfully');
      })
      .catch((error: any) => {
        console.error('Error downloading excel:', error);
        toast.error('Failed to download Excel file');
      });
  };

  // Use data and summary directly from API response (server-side filtered)
  const filteredData = reportData?.data;
  const displaySummary = reportData?.summary;

  // Toggle ledger breakdown
  const toggleRow = (costCentreId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(costCentreId)) {
      newExpanded.delete(costCentreId);
    } else {
      newExpanded.add(costCentreId);
    }
    setExpandedRows(newExpanded);
  };

  // Filter handlers
  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  return (
    <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2 , mt: 2 }} className={'c-card2'}>
      {/* Scrollable content area */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        <ReusableReportHeader
          reportType="COST_CENTRE"
          title="Cost Centre Report"
          subtitle={(filterData && filterData.periodString) ? filterData.periodString : 'Income and Expense Analysis by Cost Centre'}
          recordCount={filteredData?.length || 0}
          isLoading={isLoading}
          onDownloadPDF={handleDownloadExcel}
          showPrint={false}
          showShare={false}
        />

        {/* Filters */}
        <Box px={2} py={1.5}>
          <Box sx={{ display: 'flex', gap: 0, alignItems: 'center' }}>
            <Filters
              type="report"
              filterHandler={filterOnChangeHandler}
              selectedFilters={filterdOnChangeHandler}
              hideFilter={true}
              hidePartySearch={true}
            />
            <CostCentreSelector
              value={selectedCostCentre}
              onChange={setSelectedCostCentre}
              label="Filter by Cost Centre"
              placeholder="Filter by Cost Centre"
            />
          </Box>
        </Box>

        {isLoading && <LinearProgress sx={{ mx: 2 }} />}

        {/* Report Content */}
        {reportData && (
          <>
            <Box px={2}>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px 16px',
                backgroundColor: '#fff'
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                      Total Cost Centres
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.5rem' }}>
                      {displaySummary?.totalCostCentres || 0}
                    </Typography>
                  </Box>
                  <AccountBalanceIcon sx={{ fontSize: 40, color: '#1976d2', opacity: 0.7 }} />
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px 16px',
                backgroundColor: '#fff'
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                      Total Income
                    </Typography>
                    <Typography variant="h5" fontWeight={600} color="success.main" sx={{ fontSize: '1.5rem' }}>
                      {Utils.formatNumber((displaySummary?.totalIncome || 0).toFixed(2))}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px 16px',
                backgroundColor: '#fff'
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                      Total Expense
                    </Typography>
                    <Typography variant="h5" fontWeight={600} color="error.main" sx={{ fontSize: '1.5rem' }}>
                      {Utils.formatNumber((displaySummary?.totalExpense || 0).toFixed(2))}
                    </Typography>
                  </Box>
                  <TrendingDownIcon sx={{ fontSize: 40, color: 'error.main', opacity: 0.7 }} />
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '12px 16px',
                backgroundColor: '#fff'
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                      Net Profit/Loss
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={600}
                      color={(displaySummary?.totalNetProfitLoss || 0) >= 0 ? 'success.main' : 'error.main'}
                      sx={{ fontSize: '1.5rem' }}
                    >
                      {Utils.formatNumber((displaySummary?.totalNetProfitLoss || 0).toFixed(2))}
                    </Typography>
                  </Box>
                  <CurrencyRupeeIcon sx={{ fontSize: 40, color: '#ff9800', opacity: 0.7 }} />
                </Stack>
              </Box>
            </Grid>
          </Grid>
          </Box>

          {/* Cost Centre Table */}
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ width: '50px' }}></StyledTableCell>
                  <StyledTableCell>Cost Centre</StyledTableCell>
                  <StyledTableCell align="center">Category</StyledTableCell>
                  <StyledTableCell align="right">Total Income</StyledTableCell>
                  <StyledTableCell align="right">Total Expense</StyledTableCell>
                  <StyledTableCell align="right">Net Profit/Loss</StyledTableCell>
                  <StyledTableCell align="center">Transactions</StyledTableCell>
                  <StyledTableCell align="center">% of Total</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData && filteredData.length > 0 ? (
                  filteredData.map((cc) => {
                    const percentageOfTotal = reportData.summary.totalExpense > 0
                      ? ((cc.totalExpense / reportData.summary.totalExpense) * 100).toFixed(2)
                      : 0;

                    return (
                      <React.Fragment key={cc.costCentreId}>
                        <StyledTableRow>
                          <StyledTableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRow(cc.costCentreId);
                              }}
                            >
                              {expandedRows.has(cc.costCentreId) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            </IconButton>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {cc.costCentreName}
                            </Typography>
                            {/* {cc.costCentreAlias && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                {cc.costCentreAlias}
                              </Typography>
                            )} */}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {cc.categoryName || '-'}
                          </StyledTableCell>
                          <StyledTableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                            {Utils.formatNumber(cc.totalIncome.toFixed(2))}
                          </StyledTableCell>
                          <StyledTableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                            {Utils.formatNumber(cc.totalExpense.toFixed(2))}
                          </StyledTableCell>
                          <StyledTableCell
                            align="right"
                            sx={{
                              color: cc.netProfitLoss >= 0 ? 'success.main' : 'error.main',
                              fontWeight: 600
                            }}
                          >
                            {Utils.formatNumber(cc.netProfitLoss.toFixed(2))}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip label={cc.totalTransactions} size="small" />
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {percentageOfTotal}%
                          </StyledTableCell>
                        </StyledTableRow>

                        {/* Ledger Breakdown (Expandable) */}
                        {expandedRows.has(cc.costCentreId) && (
                          <StyledTableRow>
                            <StyledTableCell colSpan={8} sx={{ backgroundColor: '#f9f9f9', p: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Ledger Breakdown:
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Ledger Name</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Debit</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Credit</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Net Amount</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Transactions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {cc.ledgers.map((ledger, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{ledger.ledgerName}</TableCell>
                                      <TableCell align="right">{Utils.formatNumber(ledger.totalDebit.toFixed(2))}</TableCell>
                                      <TableCell align="right">{Utils.formatNumber(ledger.totalCredit.toFixed(2))}</TableCell>
                                      <TableCell align="right">{Utils.formatNumber(ledger.netAmount.toFixed(2))}</TableCell>
                                      <TableCell align="center">{ledger.transactionCount}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </StyledTableCell>
                          </StyledTableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No cost centre data found for the selected period
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          </>
        )}

        {!reportData && !isLoading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <AccountBalanceIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Select filters and click "Generate Report" to view cost centre analysis
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CostCentreReportPage;
